import { ROLE_LABELS, type Role } from '@lia/contracts';
import { Command } from 'cmdk';
import { Alert02Icon, Building06Icon, PlusSignIcon, Tick02Icon, UnfoldMoreIcon } from '@hugeicons/core-free-icons';
import { Icon } from '../primitives/icon.js';
import { Popover as PopoverPrimitive } from 'radix-ui';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { controlBase } from '../primitives/input.js';

export interface WorkspaceSummary {
  id: string;
  name: string;
  role: Role;
  /** Setup checklist progress; null when unknown. */
  setup?: { completed: number; total: number } | null;
  /** Items needing attention; null/undefined when unknown (never shown as 0). */
  attentionCount?: number | null;
}

export interface WorkspaceSwitcherProps {
  workspaces: readonly WorkspaceSummary[];
  currentId: string | null;
  onSelect: (id: string) => void;
  /** Shows "Crear espacio" when provided. */
  onCreate?: () => void;
  loading?: boolean;
  className?: string;
}

function WorkspaceMeta({ w }: { w: WorkspaceSummary }) {
  const setupDone = w.setup && w.setup.completed >= w.setup.total;
  return (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-meta text-text-secondary">
      <span>{ROLE_LABELS[w.role]}</span>
      {w.setup ? (
        <span className="ef-tabular">
          {setupDone ? 'Configuración completa' : `Configuración ${w.setup.completed} de ${w.setup.total}`}
        </span>
      ) : null}
      {w.attentionCount ? (
        <span className="inline-flex items-center gap-1 font-medium text-warning">
          <Icon icon={Alert02Icon} aria-hidden className="size-3.5" />
          {w.attentionCount === 1 ? '1 requiere atención' : `${w.attentionCount} requieren atención`}
        </span>
      ) : null}
    </span>
  );
}

/** Header control to switch between workspaces (developers) the user belongs to. */
export const WorkspaceSwitcher = React.forwardRef<HTMLButtonElement, WorkspaceSwitcherProps>(function WorkspaceSwitcher(
  { workspaces, currentId, onSelect, onCreate, loading, className },
  ref,
) {
  const [open, setOpen] = React.useState(false);
  const current = workspaces.find((w) => w.id === currentId);
  const attention = workspaces.reduce((n, w) => n + (w.id !== currentId && w.attentionCount ? w.attentionCount : 0), 0);
  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger
        ref={ref}
        disabled={loading}
        aria-label={current ? `Espacio de trabajo: ${current.name}. Cambiar espacio` : 'Seleccionar espacio de trabajo'}
        className={cn(controlBase, 'flex h-10 w-auto max-w-72 items-center gap-2 border-border-subtle text-left', className)}
      >
        <Icon icon={Building06Icon} aria-hidden className="size-[18px] shrink-0 text-text-secondary" />
        <span className="min-w-0 flex-1 truncate font-medium">{loading ? 'Cargando…' : (current?.name ?? 'Seleccionar espacio')}</span>
        {attention > 0 ? (
          <span className="inline-flex items-center gap-0.5 rounded-pill bg-warning-bg px-1.5 text-meta font-semibold text-warning">
            <Icon icon={Alert02Icon} aria-hidden className="size-3" />
            {attention}
            <span className="sr-only"> pendientes en otros espacios</span>
          </span>
        ) : null}
        <Icon icon={UnfoldMoreIcon} aria-hidden className="size-4 shrink-0 text-text-secondary" />
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          className="z-50 w-[min(360px,calc(100vw-32px))] rounded-menu border border-border-subtle bg-surface-default p-1 shadow-overlay animate-fade-in"
        >
          <Command label="Espacios de trabajo" loop>
            <Command.Input autoFocus placeholder="Buscar espacio…" aria-label="Buscar espacio" className={cn(controlBase, 'mb-1 h-9')} />
            <Command.List className="max-h-80 overflow-auto">
              <Command.Empty className="px-3 py-3 text-body text-text-secondary">Ningún espacio coincide.</Command.Empty>
              {workspaces.map((w) => (
                <Command.Item
                  key={w.id}
                  value={w.id}
                  keywords={[w.name]}
                  onSelect={() => {
                    onSelect(w.id);
                    setOpen(false);
                  }}
                  className="flex cursor-pointer items-start gap-2 rounded-control px-3 py-2 outline-none data-[selected=true]:bg-surface-subtle"
                >
                  <Icon icon={Tick02Icon} aria-hidden className={cn('mt-0.5 size-4 shrink-0 text-brand-primary', w.id === currentId ? 'opacity-100' : 'opacity-0')} />
                  <span className="grid min-w-0 gap-0.5">
                    <span className="truncate text-body font-medium text-text-primary">
                      {w.name}
                      {w.id === currentId ? <span className="sr-only"> (actual)</span> : null}
                    </span>
                    <WorkspaceMeta w={w} />
                  </span>
                </Command.Item>
              ))}
            </Command.List>
          </Command>
          {onCreate ? (
            <div className="mt-1 border-t border-border-subtle pt-1">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onCreate();
                }}
                className="flex w-full items-center gap-2 rounded-control px-3 py-2 text-body font-medium text-brand-primary hover:bg-surface-subtle focus-visible:outline-2 focus-visible:outline-focus"
              >
                <Icon icon={PlusSignIcon} aria-hidden className="size-4" />
                Crear espacio
              </button>
            </div>
          ) : null}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
});
